import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getEvolvedPlayers } from "../../api/savedEvolutions/getEvolvedPlayers";
import { deleteSavedEvolutionPlayer, getSavedEvolutionPlayers, saveEvolutionPlayer, updateSavedEvolutionPlayerState, } from "../../api/savedEvolutions/savedEvolutionPlayers";
import { fetchEvolutions } from "./evolutionsSlice";
const initialState = {
    myEvolvedPlayers: [],
    myEvolvedPlayersStatus: "idle",
    savedEvolutionPlayers: [],
    savedEvolutionPlayersStatus: "idle",
};
export const getMyEvolutionPlayers = createAsyncThunk("get-evolution-players", async ({ sort, playerRoleId, }) => {
    return getEvolvedPlayers({ sort, playerRoleId });
});
export const fetchSavedEvolutionPlayers = createAsyncThunk("fetch-saved-evolution-players", 
// `| void` is how RTK makes the argument optional.
async (sort) => {
    return getSavedEvolutionPlayers(sort || undefined);
});
/** A base card and a non-empty chain: anything less has nothing to save. */
export const canSaveEvolution = ({ resourceId, evolutionPath, }) => !!resourceId && !!evolutionPath?.length;
/**
 * Saves a chain, then refreshes the saved list if one is loaded, with the sort
 * it was loaded with. Login gating and feedback stay with each app.
 */
export const saveEvolution = createAsyncThunk("save-evolution", async (params, { dispatch, getState }) => {
    await saveEvolutionPlayer(params);
    const { savedEvolutionPlayersStatus, savedEvolutionPlayersSort } = getState().evolvedPlayersReducer;
    if (savedEvolutionPlayersStatus !== "idle")
        dispatch(fetchSavedEvolutionPlayers(savedEvolutionPlayersSort));
});
export const removeSavedPlayer = createAsyncThunk("remove-saved-player", async ({ id }, { dispatch }) => {
    await deleteSavedEvolutionPlayer({ id, complete: false });
    dispatch(fetchEvolutions());
    return id;
});
export const completeSavedPlayer = createAsyncThunk("complete-saved-player", async ({ id }, { dispatch }) => {
    await deleteSavedEvolutionPlayer({ id, complete: true });
    dispatch(fetchEvolutions());
    return id;
});
export const setSavedPlayerState = createAsyncThunk("set-saved-player-state", async ({ id, state }, { dispatch }) => {
    await updateSavedEvolutionPlayerState({ id, state });
    dispatch(fetchEvolutions());
    return { id, state };
});
const evolvedPlayersSlice = createSlice({
    name: "evolvedPlayers",
    initialState,
    reducers: {
        /** Both lists belong to the account; called on sign-out. */
        resetEvolvedPlayers: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMyEvolutionPlayers.pending, (state, action) => {
            state.myEvolvedPlayersStatus = "pending";
            state.myEvolvedPlayersRequestId = action.meta.requestId;
        })
            .addCase(getMyEvolutionPlayers.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.myEvolvedPlayersRequestId)
                return;
            state.myEvolvedPlayersStatus = "fulfilled";
            state.myEvolvedPlayers = action.payload;
        })
            .addCase(getMyEvolutionPlayers.rejected, (state, action) => {
            if (action.meta.requestId !== state.myEvolvedPlayersRequestId)
                return;
            state.myEvolvedPlayersStatus = "rejected";
        })
            .addCase(fetchSavedEvolutionPlayers.pending, (state, action) => {
            state.savedEvolutionPlayersStatus = "pending";
            state.savedEvolutionPlayersSort = action.meta.arg || undefined;
            state.savedEvolutionPlayersRequestId = action.meta.requestId;
        })
            .addCase(fetchSavedEvolutionPlayers.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.savedEvolutionPlayersRequestId)
                return;
            state.savedEvolutionPlayersStatus = "fulfilled";
            state.savedEvolutionPlayers = action.payload;
        })
            .addCase(fetchSavedEvolutionPlayers.rejected, (state, action) => {
            if (action.meta.requestId !== state.savedEvolutionPlayersRequestId)
                return;
            state.savedEvolutionPlayersStatus = "rejected";
        })
            .addCase(removeSavedPlayer.fulfilled, (state, action) => {
            state.savedEvolutionPlayers = state.savedEvolutionPlayers.filter((p) => p.id !== action.payload);
        })
            .addCase(completeSavedPlayer.fulfilled, (state, action) => {
            state.savedEvolutionPlayers = state.savedEvolutionPlayers.filter((p) => p.id !== action.payload);
        })
            .addCase(setSavedPlayerState.fulfilled, (state, action) => {
            const saved = state.savedEvolutionPlayers.find((p) => p.id === action.payload.id);
            if (saved)
                saved.state = action.payload.state ?? undefined;
        });
    },
});
export const { resetEvolvedPlayers } = evolvedPlayersSlice.actions;
export const evolvedPlayersSelector = (state) => state.evolvedPlayersReducer;
export const checkIfPlayerIsInEvolvedPlayersSelector = ({ savedPlayerId, resourceId: resourceIdToCheck, }) => (state) => {
    let resourceId = resourceIdToCheck;
    if (savedPlayerId) {
        resourceId = state.evolvedPlayersReducer.savedEvolutionPlayers.find((p) => p.id === savedPlayerId)?.basePlayer?.resourceId;
    }
    return state.evolvedPlayersReducer.myEvolvedPlayers.some((p) => p.resourceId === resourceId);
};
export const evolvedPlayersReducer = evolvedPlayersSlice.reducer;
//# sourceMappingURL=evolvedPlayersSlice.js.map